<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import FilterGroup from 'dashboard/components-next/filter/FilterGroup.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const props = defineProps({
  filterTypes: { type: Array, required: true, default: () => [] },
});

const emit = defineEmits(['applyFilter', 'close', 'clearFilters', 'change']);

const filters = defineModel({
  type: Array,
  default: () => [],
});

const { t } = useI18n();

const filterGroups = ref([]);
const copyPreviousGroup = ref(false);
const globalGroupOperator = ref('and');
const groupCounter = ref(0);

const createDefaultFilter = () => ({
  attributeKey: '',
  filterOperator: 'equal_to',
  values: [],
});

const createDefaultGroup = () => ({
  id: groupCounter.value++,
  internalOperator: 'and',
  filters: [createDefaultFilter()],
  groupOperator: globalGroupOperator.value || 'and',
});

const cloneGroup = (group) => ({
  id: groupCounter.value++,
  internalOperator: group.internalOperator,
  filters: group.filters.map(filter => ({
    ...filter,
    values: Array.isArray(filter.values) ? [...filter.values] : filter.values,
    queryOperator: filter.queryOperator || 'and',
  })),
  groupOperator: null,
});

const convertFiltersToGroups = (flatFilters) => {
  if (!flatFilters || flatFilters.length === 0) {
    return [createDefaultGroup()];
  }

  const hasGroupIndex = flatFilters.some(f => f.groupIndex !== undefined);

  if (hasGroupIndex) {
    const groupsMap = {};

    flatFilters.forEach(filter => {
      const idx = filter.groupIndex ?? 0;
      if (!groupsMap[idx]) {
        groupsMap[idx] = {
          id: groupCounter.value++,
          internalOperator: filter.queryOperator || 'and',
          filters: [],
          groupOperator: null,
        };
      }
      groupsMap[idx].filters.push({
        attributeKey: filter.attributeKey,
        filterOperator: filter.filterOperator,
        values: Array.isArray(filter.values) ? [...filter.values] : filter.values,
        queryOperator: filter.queryOperator || 'and',
      });
    });

    const groupIndices = Object.keys(groupsMap).map(Number).sort((a, b) => a - b);
    const groups = groupIndices.map((idx, i) => {
      const group = groupsMap[idx];
      if (group.filters.length > 0) {
        group.internalOperator = group.filters[0].queryOperator || 'and';
      }

      if (i < groupIndices.length - 1) {
        const lastFilter = group.filters[group.filters.length - 1];
        group.groupOperator = lastFilter.queryOperator || 'or';
      }
      return group;
    });

    if (groups.length > 0) {
      const lastGroupIndex = groups.length - 1;
      const previousGroupOperator = lastGroupIndex > 0 ? groups[lastGroupIndex - 1].groupOperator : null;
      groups[lastGroupIndex].groupOperator = previousGroupOperator || globalGroupOperator.value || 'and';
    }

    if (groups.length > 1 && groups[0].groupOperator) {
      globalGroupOperator.value = groups[0].groupOperator;
    }

    return groups.length > 0 ? groups : [createDefaultGroup()];
  }

  const internalOperator = flatFilters[0]?.queryOperator || 'and';
  return [{
    id: groupCounter.value++,
    internalOperator,
    filters: flatFilters.map(filter => ({
      attributeKey: filter.attributeKey,
      filterOperator: filter.filterOperator,
      values: Array.isArray(filter.values) ? [...filter.values] : filter.values,
      queryOperator: filter.queryOperator || 'and',
    })),
    groupOperator: null,
  }];
};

const convertGroupsToFilters = (groups) => {
  const flatFilters = [];

  groups.forEach((group, groupIndex) => {
    group.filters.forEach((filter, filterIndex) => {
      if (!filter.attributeKey) return;

      let queryOperator = null;
      const isLastFilterInGroup = filterIndex === group.filters.length - 1;
      const isLastGroup = groupIndex === groups.length - 1;

      if (!isLastFilterInGroup) {
        queryOperator = group.internalOperator || 'and';
      } else if (!isLastGroup) {
        queryOperator = globalGroupOperator.value || 'or';
      }

      flatFilters.push({
        attributeKey: filter.attributeKey,
        filterOperator: filter.filterOperator,
        values: filter.values,
        queryOperator,
        groupIndex,
      });
    });
  });

  return flatFilters;
};

// Inicializar grupos a partir dos filtros
filterGroups.value = convertFiltersToGroups(filters.value);

// Watch apenas para atualizar grupos quando filters mudar externamente
let isInternalUpdate = false;
watch(() => filters.value, (newFilters) => {
  if (!isInternalUpdate) {
    filterGroups.value = convertFiltersToGroups(newFilters);
  }
}, { deep: true });

// Watch para sincronizar quando o conteúdo dos grupos mudar
watch(() => filterGroups.value, () => {
  if (!isInternalUpdate) {
    syncFiltersFromGroups();
  }
}, { deep: true });

// Método para sincronizar grupos -> filtros
const syncFiltersFromGroups = () => {
  isInternalUpdate = true;
  const flatFilters = convertGroupsToFilters(filterGroups.value);
  filters.value = flatFilters;
  emit('change', flatFilters);
  // Usar setTimeout para garantir que a flag seja resetada após a atualização
  setTimeout(() => {
    isInternalUpdate = false;
  }, 0);
};

const resetFilter = () => {
  filterGroups.value = [createDefaultGroup()];
  globalGroupOperator.value = 'or';
  syncFiltersFromGroups();
  emit('clearFilters');
};

const addGroup = () => {
  if (filterGroups.value.length > 0) {
    filterGroups.value[filterGroups.value.length - 1].groupOperator = globalGroupOperator.value;
  }

  if (copyPreviousGroup.value && filterGroups.value.length > 0) {
    const lastGroup = filterGroups.value[filterGroups.value.length - 1];
    const clonedGroup = cloneGroup(lastGroup);
    clonedGroup.groupOperator = globalGroupOperator.value;
    filterGroups.value.push(clonedGroup);
  } else {
    const newGroup = createDefaultGroup();
    newGroup.groupOperator = globalGroupOperator.value;
    filterGroups.value.push(newGroup);
  }

  syncFiltersFromGroups();
};

const updateGlobalGroupOperator = (newOperator) => {
  globalGroupOperator.value = newOperator;

  filterGroups.value.forEach((group) => {
    group.groupOperator = newOperator;
  });

  syncFiltersFromGroups();
};

const removeGroup = (groupIndex) => {
  if (filterGroups.value.length === 1) {
    resetFilter();
  } else {
    filterGroups.value.splice(groupIndex, 1);

    filterGroups.value.forEach((group) => {
      group.groupOperator = globalGroupOperator.value;
    });

    syncFiltersFromGroups();
  }
};

const isConditionsValid = () => {
  return filterGroups.value.every(group =>
    group.filters.every(filter => {
      if (!filter.attributeKey) return false;

      const filterType = props.filterTypes.find(ft => ft.attributeKey === filter.attributeKey);
      if (!filterType) return false;

      const operator = filterType.filterOperators?.find(op => op.value === filter.filterOperator);
      if (!operator) return false;

      if (!operator.hasInput) return true;

      const inputType = operator.inputOverride ?? filterType.inputType;

      if (inputType === 'multiSelect') {
        return Array.isArray(filter.values) && filter.values.length > 0;
      } else if (['searchSelect', 'booleanSelect'].includes(inputType)) {
        return filter.values && typeof filter.values === 'object' && filter.values.id !== undefined;
      } else {
        return filter.values !== '' && filter.values !== null && filter.values !== undefined;
      }
    })
  );
};

const canApplyFilters = computed(() => {
  return isConditionsValid();
});
</script>

<template>
  <div class="table-filter-container">
   

    <!-- Filter Groups -->
    <div class="filter-groups">
      <template v-for="(group, groupIndex) in filterGroups" :key="group.id">
        <FilterGroup
          v-model:filters="group.filters"
          v-model:internal-operator="group.internalOperator"
          v-model:group-operator="group.groupOperator"
          :group-index="groupIndex"
          :is-only-group="filterGroups.length === 1"
          :filter-types="filterTypes"
          :show-operator="groupIndex > 0"
          :is-first-operator="groupIndex === 1"
          @remove="removeGroup(groupIndex)"
          @update:group-operator="groupIndex === 1 ? updateGlobalGroupOperator($event) : null"
        />
      </template>
    </div>

    <!-- Actions -->
    <div class="filter-actions">
      <Button 
        sm 
        ghost 
        blue 
        icon="i-lucide-layers" 
        @click="addGroup"
        class="add-group-btn"
      >
        {{ t('FILTER.ADD_FILTER_GROUP') }}
      </Button>
      
      <label class="copy-previous-label">
        <Switch v-model="copyPreviousGroup" size="small" />
        <span class="copy-previous-text">
          {{ t('FILTER.COPY_PREVIOUS_GROUP') }}
        </span>
      </label>
    </div>

    <slot></slot>
  </div>
</template>

<style scoped>
/* Container principal */
.table-filter-container {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  width: 100%;
}

/* Header */
.filter-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.filter-title {
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.5rem;
  color: rgb(var(--n-slate-12));
  margin: 0;
}

/* Filter Groups */
.filter-groups {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

/* Actions */
.filter-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgb(var(--n-weak));
  flex-wrap: wrap;
}

.add-group-btn {
  white-space: nowrap;
}

/* Copy Previous Toggle */
.copy-previous-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  user-select: none;
}

.copy-previous-text {
  font-size: 0.75rem;
  line-height: 1rem;
  color: rgb(var(--n-slate-11));
  white-space: nowrap;
}

/* Hover states */
.copy-previous-label:hover .copy-previous-text {
  color: rgb(var(--n-slate-12));
}

/* Responsive */
@media (max-width: 640px) {
  .filter-actions {
    flex-direction: column;
    align-items: stretch;
    gap: 0.75rem;
  }

  .add-group-btn {
    width: 100%;
  }

  .copy-previous-label {
    width: 100%;
  }

  .copy-previous-text {
    font-size: 0.7rem;
  }
}
</style>